import { Component, State, Prop, Watch, Event, EventEmitter, Method, Listen, Element, h } from '@stencil/core';
import "../../../ionic/ionic.module";

import { ExpanderEvent } from './expander.model';
import { AccordionEvent } from './../ibl-accordion/ibl-accordion.model';

@Component({
  tag: 'ibl-expander',
  styleUrl: 'ibl-expander.css',
  shadow: false
})
export class IblExpander {
  @Element() hostElement: HTMLElement;
  contentRef;

  @Prop({ mutable: true }) public isOpen: boolean = true;
  @Prop() defaultHeaderAction: 'toggle' | 'open' | 'close' | 'none' = 'toggle';
  @Prop() defaultFooterAction: 'toggle' | 'open' | 'close' | 'none' = 'toggle';

  @Event() expanderToggle: EventEmitter<ExpanderEvent>;
  @Event() expanderOpen: EventEmitter<ExpanderEvent>;
  @Event() expanderClose: EventEmitter<ExpanderEvent>;
  @Event() clickHeader: EventEmitter<MouseEvent>;
  @Event() clickFooter: EventEmitter<MouseEvent>;

  @Listen('closeExpanders') closeExpanders(e: CustomEvent<AccordionEvent>) {
    if (this.isOpen && (this !== e.detail.exclude)) {
      this.isOpen = false;
    }
  }

  @Watch('isOpen') public isOpenWillChange(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      const evPayload = { sender: this, data: newValue };
      this.expanderToggle.emit(evPayload);
      if (!newValue) {
        this.collapseSection(this.contentRef);
        this.expanderClose.emit(evPayload);
      } else {
        this.expanderOpen.emit(evPayload);
        this.expandSection(this.contentRef);
      }
    }
  }

  @Method() async toggle() {
    this.isOpen = !this.isOpen;
  }
  @Method() async open() {
    this.isOpen = true;
  }
  @Method() async close() {
    this.isOpen = false;
  }
  @Method() async getIsOpen() {
    return this.isOpen;
  }

  headerClickHeader(e) {
    this.clickHeader.emit(e);
    switch (this.defaultHeaderAction) {
      case 'toggle':
        this.toggle();
        break;

      case 'open':
        this.open();
        break;

      case 'close':
        this.close();
        break;
    }
  }

  headerClickFooter(e) {
    this.clickFooter.emit(e);
    switch (this.defaultFooterAction) {
      case 'toggle':
        this.toggle();
        break;

      case 'open':
        this.open();
        break;

      case 'close':
        this.close();
        break;
    }
  }

  componentWillLoad() {
  }

  componentDidLoad() {
    if (!this.isOpen) {
      this.contentRef.style.height = '0px';
    } else {
      this.contentRef.style.height = null;
    }
  }

  collapseSection(element) {
    // get the height of the element's inner content, regardless of its actual size
    const sectionHeight = element?.scrollHeight;

    // temporarily disable all css transitions
    const elementTransition = element.style.transition;
    element.style.transition = '';

    // on the next frame (as soon as the previous style change has taken effect),
    // explicitly set the element's height to its current pixel height, so we
    // aren't transitioning out of 'auto'
    requestAnimationFrame(() => {
      element.style.height = sectionHeight + 'px';
      element.style.transition = elementTransition;

      // on the next frame (as soon as the previous style change has taken effect),
      // have the element transition to height: 0
      requestAnimationFrame(() => {
        element.style.height = '0px';
      });
    });
  }

  expandSection(element, listenEvents = true) {
    // get the height of the element's inner content, regardless of its actual size
    const sectionHeight = element?.scrollHeight;

    // have the element transition to the height of its inner content
    element.style.height = sectionHeight + 'px';

    if (listenEvents) {
      // when the next css transition finishes (which should be the one we just triggered)
      const transitEv = (e) => {
        // remove this event listener so it only gets triggered once
        element.removeEventListener('transitionend', transitEv);

        // remove "height" from the element's inline styles, so it can return to its initial value
        element.style.height = null;
      }
      element.addEventListener('transitionend', transitEv);
    }
  }

  renderHeader() {
    return (
      <div class='expander expander-header' onClick={(e) => this.headerClickHeader(e)}>
        <slot name="header">
        </slot>
      </div>
    );
  }

  renderContent() {
    let contentClass;
    if (this.isOpen) {
      contentClass = 'expander-content-open';
    } else {
      contentClass = 'expander-content-closed';
    }

    return (
      <div class={`expander expander-content ${contentClass}`} ref={(ref) => this.contentRef = ref}>
        <slot name="content">
        </slot>
      </div>
    );
  }

  renderFooter() {
    return (
      <div class='expander expander-footer' onClick={(e) => this.headerClickFooter(e)}>
        <slot name="footer">
        </slot>
      </div>
    );
  }

  render() {
    return (
      <ion-card class="ion-margin-bottom">
        {this.renderHeader()}

        {this.renderContent()}

        {this.renderFooter()}
      </ion-card>
    );
  }
}
